import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { Mail, Gift, CheckCircle2, Loader2 } from "lucide-react";
import { useLang, STR } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_COUNT = 14892;

export default function Waitlist() {
  const { lang } = useLang();
  const t = STR[lang];
  const locale = lang === "en" ? "en-US" : lang === "es" ? "es-ES" : "pt-BR";
  const [email, setEmail] = useState("");
  const [platform, setPlatform] = useState("ambos");
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const platforms = [
    { value: "ios", label: "iOS" },
    { value: "android", label: "Android" },
    { value: "ambos", label: t.vip_both },
  ];

  useEffect(() => {
    axios
      .get(`${API}/waitlist/count`)
      .then((r) => setCount(r.data.count || 0))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/waitlist`, { email, platform });
      setCount(r.data.count || 0);
      setDone(true);
      confetti({
        particleCount: 140,
        spread: 80,
        origin: { y: 0.7 },
        colors: ["#38bdf8", "#2e6bff", "#fbbf24", "#ffffff"],
      });
      toast.success(r.data.already_registered ? t.toast_dup : t.toast_ok);
    } catch (err) {
      toast.error(err.response?.data?.detail || t.toast_err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="vip" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_50%,rgba(251,191,36,0.07),transparent_70%)] pointer-events-none" />

      <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <span className="mono inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-amber-300 border border-amber-400/30 bg-amber-400/10 rounded-full px-4 py-2 mb-7">
            <Gift className="w-3.5 h-3.5" />
            {t.vip_badge}
          </span>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
            {t.vip_h2a}{" "}
            <span className="bg-gradient-to-r from-amber-300 to-orange-400 bg-clip-text text-transparent">
              {t.vip_h2b}
            </span>{" "}
            {t.vip_h2c}
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed max-w-xl mx-auto">
            {t.vip_sub}
          </p>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="waitlist-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mt-10 rounded-3xl border border-white/10 bg-[#0c1122]/80 backdrop-blur-xl p-6 sm:p-8 shadow-2xl"
        >
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div
                key="success"
                data-testid="waitlist-success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                className="py-6 flex flex-col items-center gap-4"
              >
                <span className="grid place-items-center w-16 h-16 rounded-full bg-emerald-400/15 border border-emerald-400/40">
                  <CheckCircle2 className="w-8 h-8 text-emerald-300" />
                </span>
                <p className="font-display font-bold text-xl text-white">{t.vip_ok_t}</p>
                <p className="text-sm text-slate-400 max-w-sm">
                  {t.vip_ok_s}
                </p>
              </motion.div>
            ) : (
              <motion.div key="form" exit={{ opacity: 0, y: -12 }} className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <label className="relative flex-1">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={lang === "pt" ? "seu@email.com" : lang === "es" ? "tu@email.com" : "you@email.com"}
                      data-testid="waitlist-email-input"
                      className="w-full rounded-2xl border border-white/10 bg-white/[0.04] pl-11 pr-4 py-4 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all"
                    />
                  </label>
                  <div
                    data-testid="waitlist-platform-select"
                    className="flex rounded-2xl border border-white/10 bg-white/[0.04] p-1"
                  >
                    {platforms.map((p) => (
                      <button
                        key={p.value}
                        type="button"
                        onClick={() => setPlatform(p.value)}
                        data-testid={`platform-option-${p.value}`}
                        className={`px-4 py-2 rounded-xl text-xs font-bold transition-all duration-200 ${
                          platform === p.value
                            ? "bg-amber-400 text-black shadow-lg shadow-amber-400/30"
                            : "text-slate-400 hover:text-white"
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="waitlist-submit-btn"
                  className="w-full rounded-2xl bg-gradient-to-r from-sky-400 via-blue-500 to-amber-400 text-black font-display font-bold text-sm sm:text-base py-4 transition-all duration-200 hover:scale-[1.02] hover:shadow-xl hover:shadow-blue-500/25 active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {t.vip_submit}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.form>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          data-testid="waitlist-counter"
          className="mono mt-6 text-[11px] uppercase tracking-[0.2em] text-slate-500"
        >
          <span className="text-amber-300 font-bold">
            {(BASE_COUNT + count).toLocaleString(locale)}
          </span>{" "}
          {t.vip_counter}
        </motion.p>
      </div>
    </section>
  );
}
